import React, { useReducer, useEffect } from 'react';
import './Chat.css';
import { useUser } from '../contexts/UserContext.js';

const initialState = {
  messages: [],
  newMessage: '',
  socket: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_MESSAGE':
      return { ...state, newMessage: action.payload };
    case 'ADD_MESSAGE':
      // Include the timestamp when adding a message
      const newMessage = {
        ...action.payload,
        timestamp: new Date().toLocaleTimeString(),
      };
      return { ...state, messages: [...state.messages, newMessage] };
    case 'SET_SOCKET':
      return { ...state, socket: action.payload };
    case 'RESET_NEW_MESSAGE':
      return { ...state, newMessage: '' };
    default:
      return state;
  }
}

const Chat = () => {
  const { user } = useUser(); // Access user info
  const [{ messages, newMessage, socket }, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY = 1000; // 1 second
    let reconnectTimeout = null;
    let currentSocket = null;

    const userId = user?.sub || user?.id || 'NoUserIdFound'; // Use Auth0 sub as userId
    const wsUrl = `wss://jtazvdr02m.execute-api.eu-north-1.amazonaws.com/development/?userId=${userId}`;
    
    const connect = () => {
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        return;
      }

      currentSocket = new WebSocket(wsUrl);
      
      currentSocket.onopen = () => {
        reconnectAttempts = 0; // Reset on successful connection
        dispatch({ type: 'SET_SOCKET', payload: currentSocket });
      };

      currentSocket.onmessage = (event) => {
        if (event.data) {
          try {
            const responseData = JSON.parse(event.data);
            if (responseData && responseData.message) {
              dispatch({ type: 'ADD_MESSAGE', payload: { text: responseData.message, sender: 'bot' } });
            }
          } catch (error) {
            // Error parsing WebSocket message
          }
        }
      };

      currentSocket.onerror = (error) => {
        // WebSocket error occurred
      };

      currentSocket.onclose = (event) => {
        dispatch({ type: 'SET_SOCKET', payload: null });
        
        // Only reconnect on abnormal closure (not user-initiated)
        if (event.code !== 1000) {
          reconnectAttempts++;
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s
          const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectAttempts - 1), 30000);
          
          reconnectTimeout = setTimeout(connect, delay);
        }
      };
    };

    connect();

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (currentSocket) {
        currentSocket.close(1000, 'Component unmounting'); // Normal closure
      }
    };
  }, [user]);

  const handleSendMessage = () => {
    if (socket && newMessage.trim() !== '') {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          action: 'sendMessage',
          userId: user.id,
          data: { input: newMessage }
        }));
        dispatch({ type: 'ADD_MESSAGE', payload: { text: newMessage, sender: 'user' } });
        dispatch({ type: 'RESET_NEW_MESSAGE' });
      }
    }
  };

  return (
    <div className="chat-container">
      <h2>Chat Page</h2>
      <div className="message-container">
        {messages.map((message, index) => (
          <div key={index} className={message.sender === 'bot' ? 'bot-message' : 'user-message'}>
            {message.text}
            <br/>
            <small className='sender-name'>{ message.sender === 'user' ? user?.name || 'User' : 'Intellegacy Bot'} &nbsp; {message.timestamp}</small>
          </div>
        ))}
      </div>
      <div className="input-container">
        <input type="text" value={newMessage} onChange={(e) => dispatch({ type: 'SET_MESSAGE', payload: e.target.value })} />
        <button onClick={handleSendMessage}>Send</button>
      </div>
    </div>
  );
};

export default Chat;